import {useEffect, useMemo, useState} from 'react';
import {FmlxIcon, FmlxTableCell, FmlxTableRow} from 'fmlx-common-ui';
import {LeadAccessorEnum, LeadFeedbackEnum, PaginationEnum, StatusConditionEnum} from '../../const/dataEnum';
import {getSortedAndSlicedData, handleSort} from '../../lib/filterHelper';
import {getLeadFeedback} from '../../stores/business/feedbackReviewBusiness';
import FeedbackModal from './FeedbackReviewModal/FeedbackModal';
import './FeedbackLeadPage.scss';
import {useColumnFormat} from '../../hooks/useColumnFormat';
import {useDataProcessor} from '../../hooks/useDataProcessor';
import EssSimpleTable from '../../components/MessageTable/TableColumn/EssSimpleTable';

export const feedbackLeadConfig = {
  projectId: 'projectId',
  projectLead: (project) => project.projectLead,
  projectName: 'projectName',
  isCollapsible: (project) => project.isCollapsible,
  members: (project) =>
    project.feedbacks.map((feedback) => ({
      id: feedback.employeeId,
      name: feedback.teamMember,
      practice: feedback.practice,
      comments: feedback.comments,
    })),
};

const FeedbackLeadPage = ({userRole, periodId}) => {
  const [feedback, setFeedback] = useState([]);
  const [dataPerColumn, setDataPerColumn] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [openRows, setOpenRows] = useState({});
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PaginationEnum.PAGE_OPTIONS[0]);
  const [sortConfig, setSortConfig] = useState({key: null, direction: undefined});

  const {columnFormat} = useColumnFormat(LeadAccessorEnum, StatusConditionEnum);
  const {dataState} = useDataProcessor(feedback, feedbackLeadConfig, {});

  const ActionIcon = Object.freeze({
    ACTIONS: {
      id: 'actions',
      accessor: 'employeSentTab',
      Cell: ({row}) => (
        <div style={{cursor: 'pointer'}} onClick={() => handleOnClick(row)}>
          <FmlxIcon name={'Eye'} />
        </div>
      ),
    },
  });

  const generateTableColumns = [
    LeadFeedbackEnum.PROJECT_NAME,
    LeadFeedbackEnum.TEAM_MEMBER,
    LeadFeedbackEnum.PRACTICE,
    LeadFeedbackEnum.COMMENTS,
    LeadFeedbackEnum.ACTION,
  ];

  useEffect(() => {
    const fetchFeedback = async () => {
      const data = await getLeadFeedback(periodId);

      if (!Array.isArray(data) || data.length === 0) {
        setFeedback([]);
        return;
      }

      const modifiedData = data.map((item) => ({
        ...item,
        projectLead: userRole,
        isCollapsible: true,
      }));

      setFeedback(modifiedData);
      setDataPerColumn(generateTableColumns);
    };
    fetchFeedback();
  }, [page, rowsPerPage, periodId]);

  const handleSetOpen = (id) => {
    setOpenRows((prev) => ({
      ...prev,
      [id]: !Boolean(prev[id]),
    }));
  };

  const hanldeOnClose = () => {
    setIsOpen(false);
    setSelectedFeedback(null);
  };

  const handleOnClick = (feedback) => {
    setSelectedFeedback(feedback);
    setIsOpen(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const startIndex = page * rowsPerPage;
    const newPage = Math.floor(startIndex / newRowsPerPage);

    setRowsPerPage(newRowsPerPage);
    setPage(newPage);
  };

  const memoizedSlicedData = useMemo(() => {
    return getSortedAndSlicedData(dataState, sortConfig, page, rowsPerPage);
  }, [dataState, sortConfig, page, rowsPerPage]);

  const onSort = (column) => {
    handleSort(column, setSortConfig);
  };

  const renderCollapsibleContent = (row) =>
    row.members.map((member) => (
      <FmlxTableRow key={member.id}>
        <FmlxTableCell colSpan={2} align={FmlxTableCell.Align.RIGHT}></FmlxTableCell>
        <FmlxTableCell>{member.name}</FmlxTableCell>
        <FmlxTableCell>{member.practice}</FmlxTableCell>
        <FmlxTableCell colSpan={1} align={FmlxTableCell.Align.CENTER}>
          {member.comments}
        </FmlxTableCell>
        <FmlxTableCell size={'small'} align={FmlxTableCell.Align.RIGHT}>
          {ActionIcon.ACTIONS.Cell({row: member})}
        </FmlxTableCell>
      </FmlxTableRow>
    ));

  return (
    <div>
      <EssSimpleTable
        // onSort={onSort}
        filteredData={memoizedSlicedData}
        columnData={dataPerColumn}
        columns={columnFormat}
        openRows={openRows}
        collapsibleContentTable={renderCollapsibleContent}
        isCollapsible={true}
        handleOnClick={handleOnClick}
        onCollapse={handleSetOpen}
        dataCount={feedback}
        page={page}
        rowsPerPage={rowsPerPage}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        onSort={onSort}
        sortTableDirection={sortConfig.direction}
        sortKey={sortConfig.key}
      />
      {isOpen && selectedFeedback && (
        <FeedbackModal open={isOpen} onClose={hanldeOnClose} selectedFeedback={selectedFeedback} />
      )}
    </div>
  );
};

export default FeedbackLeadPage;
